import React, { useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import { es } from "date-fns/locale";
import SearchIcon from "@material-ui/icons/Search";
import { useForm } from "react-hook-form";
import { Filter } from "@material-ui/icons";
import { isDate } from "moment";

const CustomTextField = ({ Name, Label, Type, ChangeFunction, Error }) => (
  <TextField
    fullWidth={true}
    variant="outlined"
    size="small"
    name={Name}
    label={Label}
    onChange={ChangeFunction}
    type={Type ?? "text"}
    inputProps={{ min: 0, autoComplete: "off" }}
    error={Error ?? ""}
  />
);

const CustomSelect = ({
  Name,
  Label,
  Value,
  Error,
  ChangeFunction,
  DataList,
}) => (
  <FormControl
    variant="outlined"
    fullWidth={true}
    size="small"
    error={Error ?? false}>
    <InputLabel>{Label}</InputLabel>
    <Select
      variant="outlined"
      value={Value}
      onChange={ChangeFunction}
      name={Name}
      label={Label}
      error={Error ?? false}>
      <MenuItem value={0}>
        <em>Todos</em>
      </MenuItem>
      {DataList &&
        DataList.map((item) => (
          <MenuItem key={item.key} value={item.value}>
            {item.text}
          </MenuItem>
        ))}
    </Select>
  </FormControl>
);

const CustomDatePicker = ({
  Name,
  Value,
  Label,
  ChangeFunction,
  Max,
  Min,
  Disabled,
}) => (
  <FormControl variant="outlined" fullWidth={true} size="small">
    <KeyboardDatePicker
      className="c-date-picker"
      format="dd/MM/yyyy"
      name={Name}
      label={Label}
      inputVariant="outlined"
      okLabel="Seleccionar"
      cancelLabel="Cancelar"
      invalidDateMessage="Fecha incorrecta"
      maxDateMessage="Fecha incorrecta"
      minDateMessage="Fecha incorrecta"
      value={Value ?? null}
      onChange={ChangeFunction}
      minDate={Min ?? false}
      maxDate={Max ?? new Date()}
      disableFuture={true}
      disabled={Disabled}
      clearable
      clearLabel="Borrar"
    />
  </FormControl>
);

export default function Agreement({
  IsSearching,
  FilterForm,
  SetFilterForm,
  DocumentTypesList,
  AgreementStatesList,
  CompaniesList,
  SubmitFunction,
  IsAdmin,
  setDataToast,
}) {
  const { handleSubmit } = useForm();
  const [statusValue, setStatusValue] = useState();
  const [errorMessage, setErrorMessage] = useState("");

  function shortError(message) {
    setDataToast({
      active: true,
      message: message,
      type: "error",
    });
  }

  function filterValidation() {
    if (IsSearching) {
      return "";
    }

    if (
      !FilterForm.documentNumber &&
      !FilterForm.documentType &&
      !FilterForm.agreementNumber &&
      !FilterForm.dateFrom &&
      !FilterForm.dateTo &&
      !FilterForm.agreementStatus === undefined
    ) {
      return "Debe ingresar algún filtro válido.";
    }

    if (
      (FilterForm.documentNumber ||
        FilterForm.documentType ||
        FilterForm.agreementNumber ||
        FilterForm.agreementStatus) &&
      (documentValidation() || documentTypeValidation())
    ) {
      if (!FilterForm.documentNumber) {
        return "Por favor, ingrese el número de documento.";
      }

      if (!FilterForm.documentType) {
        return "Por favor, seleccione el tipo de documento.";
      }
    } else if (!documentValidation() && !documentTypeValidation()) {
      if (
        FilterForm.agreementNumber ||
        (!IsAdmin && (!FilterForm.dateFrom || !FilterForm.dateTo))
      ) {
        return "";
      }

      if (
        !FilterForm.agreementStatus === undefined &&
        !FilterForm.documentNumber &&
        !FilterForm.agreementNumber &&
        !dateFromValidation() &&
        !dateToValidation()
      ) {
        return "Debe ingresar un estado y fechas válidas.";
      }
    }

    return "";
  }

  function documentValidation() {
    return FilterForm.documentNumber ?? false;
  }

  function documentTypeValidation() {
    return FilterForm.documentType ?? false;
  }

  function dateFromValidation() {
    return isDate(FilterForm.dateFrom) ?? false;
  }

  function dateToValidation() {
    return isDate(FilterForm.dateTo) ?? false;
  }

  function setFilterData(e) {
    if (
      e.target.name === "agreementNumber" ||
      e.target.name === "documentNumber"
    ) {
      e.target.value = e.target.value.replace(/[^0-9]/g, "");
    }

    if (e.target.name === "agreementStatus") {
      setStatusValue(e.target.value);
    }
    setData(e.target.name, e.target.value === 0 ? "" : e.target.value);
  }

  function setData(name, value) {
    SetFilterForm({ ...FilterForm, [name]: value });
  }

  function handleSearch(e) {
    e.preventDefault();
    const error = filterValidation();
    if (error) {
      shortError(error);
      setErrorMessage(error);
      return;
    }
    setErrorMessage("");
    SubmitFunction();
  }

  return (
    <form onSubmit={handleSearch}>
      <Grid container spacing={1}>
        <Grid item md={3} xs={12}>
          <TextField
            fullWidth={true}
            variant="outlined"
            size="small"
            name={"agreementNumber"}
            label={"Número del contrato"}
            onChange={setFilterData}
            inputProps={{ min: 0, autoComplete: "off" }}
            value={FilterForm.agreementNumber}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          {FilterForm.documentType !== undefined && (
            <CustomSelect
              Name="documentType"
              Label="Tipo de documento"
              Value={FilterForm.documentType}
              ChangeFunction={setFilterData}
              Error={FilterForm.documentNumber && !FilterForm.documentType}
              DataList={DocumentTypesList}
            />
          )}
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            fullWidth={true}
            variant="outlined"
            size="small"
            name={"documentNumber"}
            label={"Número del documento"}
            onChange={setFilterData}
            inputProps={{ min: 0, autoComplete: "off" }}
            error={FilterForm.documentType && !FilterForm.documentNumber}
            value={FilterForm.documentNumber}
          />
        </Grid>
        <Grid item md={IsAdmin ? 4 : 2} xs={12}>
          <CustomSelect
            Name="agreementStatus"
            Label="Estado"
            ChangeFunction={setFilterData}
            DataList={AgreementStatesList}
          />
        </Grid>
        {IsAdmin && (
          <>
            <Grid item md={4} xs={12}>
              <CustomSelect
                Name="companyId"
                Label="Compañía"
                ChangeFunction={setFilterData}
                DataList={CompaniesList}
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <CustomDatePicker
                Name="dateFrom"
                Label="Fecha desde"
                Value={FilterForm.dateFrom}
                Max={FilterForm.dateTo}
                ChangeFunction={(e, v) => setData("dateFrom", e)}
                clearable
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <CustomDatePicker
                Name="dateTo"
                Label="Fecha hasta"
                Value={FilterForm.dateTo}
                Min={FilterForm.dateFrom}
                ChangeFunction={(e, v) => setData("dateTo", e)}
                clearable
              />
            </Grid>
            <Grid item md={2} />
          </>
        )}
        <Grid item md={2} xs={12}>
          <Button
            className="c-btn-info c-mt-2"
            variant="contained"
            fullWidth={true}
            type="submit"
            startIcon={<SearchIcon />}>
            Buscar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
